import { Box, Button, Paper } from '@mui/material'
import { formatRFC3339DateTimeOrEmpty } from '~/shared/lib/date'
import { useState } from 'react'
import { Loader } from '~/shared/ui'
import { useUpdateTask } from './TaskService'
import type { PatientUser } from '~/shared/types'
import type { InlineTaskItem } from './InlineTask'
import type { TaskFormDraft } from './taskTypes'

import InlineTaskEditor from './InlineTaskEditor'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

interface SingleTaskEditorProps {
  handleClose: () => void
  patient: PatientUser
  task: InlineTaskItem
}

const SingleTaskEditor = ({ handleClose, patient, task }: SingleTaskEditorProps) => {
  const { mutateAsync: updateTask } = useUpdateTask()
  const [loading, setLoading] = useState(false)
  const [editedTask, setEditedTask] = useState<TaskFormDraft>(() => ({
    title: task.title ?? '',
    due_date: task.due_date || '',
    owner_group: task.owner_group ?? undefined,
    priority: task.priority ?? 0,
    is_complete: false,
  }))

  const handleUpdate = () => {
    setLoading(true)
    fireAndForget('[Task] handleUpdate', async () => {
      try {
        await updateTask({
          id: task.id,
          title: editedTask.title,
          priority: editedTask.priority,
          owner_group: editedTask.owner_group,
          is_complete: editedTask.is_complete,
          due_date: formatRFC3339DateTimeOrEmpty(editedTask.due_date),
        })
        handleClose()
      } catch (e) {
        logger.warn('[Task] update task', e)
        setLoading(false)
      }
    })
  }

  return (
    <Paper>
      <Box sx={{ p: 2 }}>
        <InlineTaskEditor task={editedTask} onChange={setEditedTask} patient={patient} />
        <Box sx={{ mt: 2, display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
          <Button disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            sx={{ ml: 1 }}
            disabled={loading || !editedTask.title}
            variant="contained"
            color="primary"
            onClick={handleUpdate}
          >
            {loading && <Loader inline />}
            <Box component="span" sx={loading ? { color: 'transparent' } : undefined}>
              Update Task
            </Box>
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

export default SingleTaskEditor
